// © 2026 AttainIP Engineering
/**
 * Tracking State Store
 * Captures UTM params on landing and provides tracking context for API requests
 * per RFC-0202 Section 10
 */

import { create } from 'zustand';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export interface TrackingState {
  utmParams: UtmParams;
  sessionId: string;

  // Actions
  captureUtm: () => void;
  getTrackingContext: () => Record<string, string>;
}

const STORAGE_KEY = 'attain_utm';
const STORAGE_EXPIRY_KEY = 'attain_utm_expiry';
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/**
 * Load first-touch UTM params from localStorage (if not expired)
 */
function loadStoredUtm(): UtmParams {
  try {
    const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
    if (expiry && Date.now() > Number(expiry)) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_EXPIRY_KEY);
      return {};
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Store first-touch UTM params with 30-day expiry
 */
function storeUtm(params: UtmParams): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    localStorage.setItem(STORAGE_EXPIRY_KEY, String(Date.now() + EXPIRY_MS));
  } catch {
    // localStorage unavailable — ignore
  }
}

/**
 * Generate or retrieve a stable session ID
 */
function getOrCreateSessionId(): string {
  const key = 'attain_session_id';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export const useTrackingStore = create<TrackingState>((set, get) => ({
  utmParams: loadStoredUtm(),
  sessionId: getOrCreateSessionId(),

  captureUtm: () => {
    const url = new URL(window.location.href);
    const params: UtmParams = {};
    let hasAny = false;

    for (const key of UTM_KEYS) {
      const value = url.searchParams.get(key);
      if (value) {
        params[key] = value;
        hasAny = true;
      }
    }

    if (!hasAny) return;

    // First-touch: only store if we don't already have UTM data
    const existing = loadStoredUtm();
    if (Object.keys(existing).length === 0) {
      storeUtm(params);
      set({ utmParams: params });
    }
  },

  getTrackingContext: () => {
    const { utmParams, sessionId } = get();
    const context: Record<string, string> = { session_id: sessionId };

    for (const [key, value] of Object.entries(utmParams)) {
      if (value) context[key] = value;
    }

    return context;
  },
}));
